import React from 'react';
import { Stack } from '@mui/material';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { DynamicConfigValues } from 'new/components/settings/VendorBuilder/DynamicConfigValues';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import Typography from 'new/elements/Typography';
interface VendorConfigProps {}

const fields = [
  {
    type: 'select',
    items: [
      { title: 'AI & Knowledge', value: 'AI & Knowledge' },
      { title: 'Collaboration & Productivity', value: 'Collaboration & Productivity' },
      { title: 'Commerce & Supply Chain', value: 'Commerce & Supply Chain' },
      { title: 'Customer Support', value: 'Customer Support' },
      { title: 'Data Platforms', value: 'Data Platforms' },
      { title: 'Databases', value: 'Databases' },
      { title: 'Engineering & DevOps', value: 'Engineering & DevOps' },
      { title: 'Filesystem', value: 'Filesystem' },
      { title: 'Finance', value: 'Finance' },
      { title: 'Marketing', value: 'Marketing' },
      { title: 'Operations', value: 'Operations' },
      { title: 'People & HR', value: 'People & HR' },
      { title: 'Sales & CRM', value: 'Sales & CRM' },
      { title: 'Security & Identity', value: 'Security & Identity' },
      { title: 'Specialized Applications', value: 'Specialized Applications' },
    ],
    label: (
      <>
        Industry Category <code>config.industryCategory</code>
      </>
    ),
    name: 'config.industryCategory',
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Industry Category(<code>vendor.config.industryCategory</code>)
        </Typography>
        <Typography variant="body2">
          Choose the type of industry this connector belongs to. The content of this field is only
          used as-is for creating the
          <code>CATEGORIES</code> section of the source/destination tile selection view.
        </Typography>
        <Typography variant="body2">
          <b>Recommendation: </b> Pick the category that matches what the connector&apos;s API is
          used for.
          <br /> If no category is good enough, select <code>Specialized Applications</code>.
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'text',
    label: (
      <>
        API Info URL <code>config.info_url</code>
      </>
    ),
    name: 'config.info_url',
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          API Info URL(<code>config.info_url</code>)
        </Typography>
        <Typography variant="body2">
          Set the link to the API documentation portal. This will be used as a general link from
          Nexla UI to API docs.
        </Typography>
        <Typography variant="body2">
          <b>Recommendation: </b> Use the link to vendor's API docs, not the vendor's marketing
          website. We want to give a link for customers who want to read the API docs for Advanced
          configuration.
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'grid',
    fields: [
      {
        type: 'checkbox',
        label: (
          <>
            Can be used as a Source?<code>config.isSource</code>{' '}
          </>
        ),
        name: 'config.isSource',
        description: (
          <Stack padding="16px">
            <Typography variant="h6">
              Includes Source Endpoints?(<code>vendor.config.isSource</code>)
            </Typography>
            <Typography variant="body2">
              Select this checkbox if you are including at least one source endpoint template.
            </Typography>
            <Typography variant="body2">
              <b>Recommendation: </b> Unless the API does not support it, please set this to true
              and configure at least two source endpoints.
            </Typography>
          </Stack>
        ),
      },
      {
        type: 'checkbox',
        label: (
          <>
            Can be used as a Destination?<code>config.isSink</code>{' '}
          </>
        ),
        name: 'config.isSink',
        description: (
          <Stack padding="16px">
            <Typography variant="h6">
              Includes Sink Endpoints?(<code>config.isSink</code>)
            </Typography>
            <Typography variant="body2">
              Select this checkbox if you are including at least one sink endpoint template.
            </Typography>
            <Typography variant="body2">
              <b>Recommendation: </b> Unless the API does not support it, please set this to true
              and configure at least two sink endpoints.
            </Typography>
          </Stack>
        ),
      },
    ],
  },
];

export const VendorInfo = (props: VendorConfigProps) => {
  return (
    <Section
      title={
        <div>
          Vendor Config (<code>vendor.config</code>)
        </div>
      }
      gap="16px"
    >
      <Typography variant="body2">
        This section contains controls for populating the <code>vendor.config</code> object.
      </Typography>
      <FormBuilder preventCleanUp fields={fields} />
      <DynamicConfigValues path="config" />
    </Section>
  );
};
